import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import styles from "./Input.module.scss";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import CrossIcon from "@Icons/CrossIcon";
import ClearInputIcon from "@Icons/ClearInputIcon";
import Calendar from "@Icons/Calendar";
import wrapperHOC from "@Utils/wrapperHOC";
import EyeHide from "@Icons/EyeHide";
import EyeShow from "@Icons/EyeShow";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { GROWTH_BOOK_FEATURE_FLAG_NAMES } from "constants/index";

interface IProps {
  value: string;
  onChange?: (value: string) => void;
  type:
    | "text"
    | "number"
    | "email"
    | "date"
    | "dropdown"
    | "tel"
    | "radio"
    | "dob"
    | "textArea"
    | "password";
  label: string;
  id?: string;
  error?: string;
  required?: boolean;
  showErrorMessage?: boolean;
  onFocus?: (value?: string) => void;
  onBlur?: (value?: string) => void;
  inputClassName?: string;
  containerClassName?: any;
  className?: any;
  rightIcon?: any;
  leftIcon?: any;
  leftIconWithValue?: boolean;
  clearable?: boolean;
  handleClear?: any;
  showClearInnput?: boolean;
  autoFocus?: boolean;
  rightIconClassName?: string;
  leftIconClassName?: string;
  readOnly?: boolean;
  onKeyDown?: any;
  onKeyUp?: any;
  isHomePageDesktop?: boolean;
  dataTestId?: string;
  disabled?: boolean;
  name?: string;
  testIdPrefix?: string;
  maxLength?: number;
  autoCorrect?: string;
  autoCapitalize?: string;
  spellCheck?: boolean;
  countryCode?: boolean;
  showPlaceholder?: boolean;
  autoComplete?: string;
  placeholder?: string;
  maxVal?: number;
  minVal?: number;
  inputMode?:
    | "text"
    | "email"
    | "tel"
    | "none"
    | "search"
    | "url"
    | "numeric"
    | "decimal"
    | undefined;
  isTextAreaInput?: boolean;
  row?: number;
  conditionallySetValue?: (
    value: string,
    setInputVal: Dispatch<SetStateAction<string>>,
    prevValue: string,
  ) => void;
  showPlaceholderAndLabel?: boolean;
  pattern?: string;
  preventNumbers?: boolean;
  isSearchLeadFormSection?: boolean;
  labelClassName?: string;
  labelActiveClassName?: string;
}
const Input = (props: IProps) => {
  const {
    value = "",
    onChange,
    type,
    label,
    id,
    error,
    required,
    showErrorMessage,
    onFocus,
    onBlur,
    containerClassName = "",
    inputClassName = "",
    className = null,
    leftIcon = null,
    rightIcon = null,
    clearable = false,
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    handleClear = () => {},
    showClearInnput = false,
    autoFocus = false,
    rightIconClassName = "",
    leftIconClassName = "",
    readOnly = false,
    isHomePageDesktop = false,
    onKeyDown,
    onKeyUp,
    dataTestId = "",
    disabled = false,
    name,
    testIdPrefix = "",
    maxLength,
    countryCode = false,
    showPlaceholder = false,
    autoComplete = "on",
    placeholder,
    autoCorrect = "off",
    autoCapitalize = "off",
    spellCheck = false,
    inputMode,
    maxVal = null,
    minVal = null,
    isTextAreaInput = false,
    row = 3,
    conditionallySetValue = null,
    showPlaceholderAndLabel = false,
    leftIconWithValue = false,
    pattern = null,
    preventNumbers = false,
    isSearchLeadFormSection = false,
    labelClassName = "",
    labelActiveClassName = "",
  } = props;
  const [inputVal, setInputVal] = useState(value || "");
  const [inputActive, setInputActive] = useState(false);
  const [inputType, setInputType] = useState(type);
  const inputRef: any = useRef();
  const showBookformValidation = useFeatureIsOn(
    GROWTH_BOOK_FEATURE_FLAG_NAMES.SHOW_BOOKFORM_VALIDATION,
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const numberRegex = /^\d+$/;
    let inputValue = e.target.value;

    if (showBookformValidation) {
      if (preventNumbers && inputType !== "number" && inputType !== "tel") {
        inputValue = inputValue.replace(/\d/g, "");
      }
    }

    if (inputType !== "number" && inputType !== "tel") {
      if (conditionallySetValue) conditionallySetValue(inputValue, setInputVal, value);
      else setInputVal(inputValue);
      onChange?.(inputValue);
    } else if (numberRegex.test(inputValue) || inputValue === "") {
      onChange?.(inputValue);
      setInputVal(inputValue);
    }
  };

  const handleOnBlur = (e: any) => {
    if (e.target.value) {
      onBlur?.(e.target.value);
    } else {
      onBlur?.();
    }
    if (!inputVal) setInputActive(false);
  };

  const handleOnFocus = (e: any) => {
    if (e.target.value) {
      onFocus?.(e.target.value);
    } else {
      onFocus?.();
    }
    setInputActive(true);
  };

  useEffect(() => {
    setInputVal(value);
    setInputActive(!!value);
  }, [value]);

  return (
    <div
      className={formatClassNames(
        styles.container,
        containerClassName,
        isSearchLeadFormSection && styles.isSearchLeadFormSection,
      )}
      {...(label && { "data-testid": label })}
    >
      {label &&
        (showPlaceholder ? (
          <></>
        ) : (
          <label
            className={formatClassNames(
              styles.label,
              inputActive || inputVal || value ? styles.active : "",
              showErrorMessage && styles.error,
              isSearchLeadFormSection && styles.searchRegionLabel,
              labelClassName,
              inputActive || inputVal || value ? labelActiveClassName : "",
            )}
            onClick={() => {
              inputRef.current?.focus();
            }}
            role="none"
            htmlFor={id}
          >
            {showPlaceholderAndLabel && !(inputActive || inputVal || value) ? placeholder : label}
            {required && <span className={styles.required}>*</span>}
          </label>
        ))}
      {leftIcon && (!value || leftIconWithValue) && (
        <div className={formatClassNames(styles.leftIcon, leftIconClassName)}>{leftIcon}</div>
      )}
      {isTextAreaInput ? (
        <textarea
          className={formatClassNames(
            className ?? styles.input,
            styles.textArea,
            showErrorMessage && styles.error,
            inputClassName,
            isSearchLeadFormSection && styles.isSearchLeadFormSection,
          )}
          rows={row}
          ref={inputRef}
          name={name}
          value={inputVal}
          placeholder={showPlaceholder ? placeholder : ""}
          onChange={handleChange}
          id={id}
          // required={required}
          onBlur={handleOnBlur}
          onFocus={handleOnFocus}
          autoComplete={isHomePageDesktop || inputType == "dropdown" ? "off" : autoComplete}
          autoFocus={autoFocus}
          readOnly={readOnly}
          onKeyDown={onKeyDown}
          onKeyUp={onKeyUp}
          maxLength={maxLength}
          data-testid={dataTestId}
          autoCorrect={autoCorrect}
          autoCapitalize={autoCapitalize}
          spellCheck={spellCheck}
          disabled={disabled}
          inputMode={inputMode}
          {...{ ...(maxVal ? { max: maxVal } : {}) }}
          {...{ ...(minVal ? { min: minVal } : {}) }}
        />
      ) : (
        <input
          className={formatClassNames(
            className ?? styles.input,
            showErrorMessage && styles.error,
            leftIcon && (!value || leftIconWithValue) && styles.inputWithLeftIcon,
            rightIcon && styles.inputWithRightIcon,
            disabled && styles.disabled,
            inputType == "dropdown" && styles.dropdown,
            countryCode && styles.countryCode,
            autoCapitalize === "on" ? styles.autoCapitalize : "",
            inputClassName,
            isSearchLeadFormSection && styles.isSearchLeadFormSection,
          )}
          ref={inputRef}
          name={name}
          type={inputType === "number" ? "text" : inputType}
          value={inputVal}
          placeholder={showPlaceholder ? placeholder : ""}
          onChange={(e) => handleChange(e)}
          id={id}
          // required={required}
          onBlur={handleOnBlur}
          onFocus={handleOnFocus}
          autoComplete={isHomePageDesktop || inputType == "dropdown" ? "off" : autoComplete}
          autoFocus={autoFocus}
          readOnly={readOnly}
          onKeyDown={onKeyDown}
          onKeyUp={onKeyUp}
          maxLength={maxLength}
          data-testid={dataTestId}
          autoCorrect={autoCorrect}
          autoCapitalize={autoCapitalize}
          spellCheck={spellCheck}
          disabled={disabled}
          inputMode={inputMode}
          {...{ ...(maxVal ? { max: maxVal } : {}) }}
          {...{ ...(minVal ? { min: minVal } : {}) }}
          {...{ ...(pattern ? { pattern } : {}) }}
        />
      )}
      {!isTextAreaInput && rightIcon && (!value || !clearable) && (
        <div className={formatClassNames(styles.rightIcon, rightIconClassName)}>{rightIcon}</div>
      )}
      {inputType === "dob" && (
        <div role="none" className={styles.rightIcon} onClick={handleOnFocus}>
          <Calendar />
        </div>
      )}
      {type === "password" && (
        <div
          role="none"
          className={styles.rightIcon}
          onClick={() => setInputType((t) => (t === "password" ? "text" : "password"))}
        >
          {inputType === "password" ? <EyeShow /> : <EyeHide />}
        </div>
      )}
      {clearable && value && inputType !== "dob" && (
        <div
          className={formatClassNames(styles.rightIcon, rightIconClassName)}
          role="none"
          onClick={() => {
            handleClear();
            setInputActive(false);
            setInputVal("");
          }}
          data-testid={`${testIdPrefix || ""}leadform-right-icon`}
        >
          <CrossIcon size={15} />
        </div>
      )}
      {showClearInnput && inputVal.length && inputType !== "dob" ? (
        <div
          className={formatClassNames(
            styles.closeIcon,
            isHomePageDesktop && styles.homepageDesktopCloseIcon,
          )}
          role="none"
          onClick={(e) => {
            e.stopPropagation();
            setInputVal("");
            onChange?.("");
            handleClear?.();
          }}
          data-testid={`${dataTestId}-clear-button`}
        >
          <ClearInputIcon />
        </div>
      ) : (
        ""
      )}
      {/* commented out for future use. Haven't removed error prop since it will be a sitewide change and could be breaking. */}
      {showErrorMessage && error && (
        // <div className={styles.error}>
        //   {required && !inputVal ? <>This field is required</> : <>{error}</>}
        // </div>
        <></>
      )}
    </div>
  );
};

export default wrapperHOC(Input, {
  componentName: "Input-Global",
  showForChina: true,
});
